import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { clipsInSections } from 'fym-shared/src/music24/musicVideoDoc';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { sectionToneClass } from '../timeline/sectionToneClass';
import { formatTime, sectionAt } from '../timeline/timelineGeometry';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The posters a section row shows (design f1: a strip of three). */
const ROW_THUMBS = 3;

/** The sections of the song in place of the clip timeline (design f1). A tap opens the section sheet. */
const SectionListComponent: React.FC<{ onOpen: (sectionIndex: number) => void }> = ({ onOpen }) => {
  const intl = useIntl();
  const { doc, songMap, footage, transport, setSectionIndex } = useMusicVideoEditor();
  const rows = useMemo(
    () =>
      songMap.sections.map((section) => {
        const clips = clipsInSections(doc, songMap.sections, [section.index]);
        return {
          section,
          count: clips.length,
          posters: clips
            .slice(0, ROW_THUMBS)
            .map((clip) => ({ id: clip.id, url: footage.lookup(clip.source)?.posterUrl ?? null })),
        };
      }),
    [doc, footage, songMap.sections]
  );
  const playing = sectionAt(songMap.sections, transport.positionSec);

  return (
    <>
      {rows.map(({ section, count, posters }) => (
        <button
          key={section.index}
          type="button"
          className={clsx(styles.phoneSection, section.index === playing?.index && styles.phoneSectionOn)}
          onClick={() => {
            setSectionIndex(section.index);
            transport.seek(section.startSec);
            onOpen(section.index);
          }}
        >
          <i className={sectionToneClass(section.kind)} />
          <span>
            <b>{section.label}</b>
            <small>
              {section.mood === null
                ? intl.formatMessage(panelMessages.phoneSectionFactsNoMood, {
                    time: formatTime(section.startSec),
                    count,
                  })
                : intl.formatMessage(panelMessages.phoneSectionFacts, {
                    time: formatTime(section.startSec),
                    mood: section.mood,
                    count,
                  })}
            </small>
          </span>
          <span className={styles.phoneStripThumbs}>
            {posters.map((poster) => (
              <span
                key={poster.id}
                style={poster.url === null ? undefined : { backgroundImage: `url("${poster.url}")` }}
              />
            ))}
          </span>
        </button>
      ))}
    </>
  );
};

export const SectionList = TypedMemo(SectionListComponent);
